import { Box, Button, Card, CardContent, IconButton, List } from '@mui/material';
import { ColDef, ColumnWidthCallbackParams, ICellRendererParams } from 'ag-grid-community';
// import 'ag-grid-community/styles//ag-grid.css';
// import 'ag-grid-community/styles//ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { debounce, flatMap, groupBy, some, uniq } from 'lodash';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useResizeListener } from '../../../../../Hooks';
import {
  MarketViewProductModel,
  MarketViewQueryCalendarStripAverageResponse,
  MarketViewQueryDailyPricesResponse,
  MarketViewQueryForwardCurveResponse,
  MarketViewQueryPriceResponse,
  MarketViewQueryRollingMonthAheadResponse,
  MarketViewQuerySettlementsResponse,
  MarketViewQueryStripAverageResponse,
} from '../../../../../Generated/Mark-It-View-Api';
import { MarkItViewQueryResult, PriceQueryType } from '../../../../../Types';
import { CurrencyHelper } from '../../../../../Helpers';
import { CalculateMatrixResponse } from '../../../../../Generated/Mark-It-View-Api';
import { MatrixGraphPoint, MatrixTermResult, narrowGraphPoints, narrowResults } from '../../../Tools/Matrix/MatrixTypes';
import MatrixTable from '../../../Tools/Matrix/MatrixTable';
import MatrixChartControls from '../../../Tools/Matrix/MatrixChartControls';
import './../../MarketView.scss';
import { StoneXDivider, StoneXGrid, StoneXRow } from '../../../../StoneX';
import { StoneXNumber } from '../../../../StoneXMui';
import { Download, LineAxis, LooksOne, LooksTwo } from '@mui/icons-material';
import { useApplicationSelector, RootState } from '../../../../../Redux/ReduxStore';
import { UserAccessState } from '../../../../../Redux/Slices/UserAccessSlice';
import StoneXChart from '../../../../StoneX/StoneXChart';

let numeral = require('numeraljs');

interface QueryDisplayProps {
  queries: MarkItViewQueryResult[];
  matrixResult?: CalculateMatrixResponse | null;
}

type QuoteDate = string;
type Price = number | null | undefined;
type GridCell = ReactNode;

interface PriceData {
  price?: Price;
  chartLabel?: string | null;
  gridCell?: GridCell;
}

type Row = { quoteDate: QuoteDate; columns: { [key: QuoteDate]: PriceData } };

type QuoteDateValues = Record<QuoteDate, PriceData>;

type Series = {
  queryId: number | string;
  label: QuoteDate;
  queryType: PriceQueryType;
  product: MarketViewProductModel;
  quoteDateValues: QuoteDateValues;
  showInChart: boolean;
  showInGrid: boolean;
};

export default function QueryDisplay(props: QueryDisplayProps) {
  const { queries, matrixResult } = props;

  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const isEmployee = userAccess.isEmployee;

  const [allQuerySeries, setAllQuerySeries] = useState<Series[]>([]);
  const [productYAxes, setProductYAxes] = useState<{ [productId: number]: 0 | 1 }>({});
  const [products, setProducts] = useState<{ [productId: number]: MarketViewProductModel }>({});

  const [columnDefs, setColumnDefs] = useState<ColDef[]>([{ headerName: 'QuoteDate', field: 'quoteDate' }]);
  const [rowData, setRowData] = useState<Row[]>([]);
  const [showingChartControls, setShowingChartControls] = useState(false);
  const [selectedPercentiles, setSelectedPercentiles] = useState<number[]>([0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]);
  const [minPercentile, setMinPercentile] = useState<number | null>(null);
  const [maxPercentile, setMaxPercentile] = useState<number | null>(null);

  const gridRef = useRef<AgGridReact>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const chartContainerRef = useRef(null);
  const chartContainerDimensions = useResizeListener(chartContainerRef);

  const chartSizeUpdate = useMemo(
    () =>
      debounce(() => {
        chartRef.current?.chart.setSize(chartContainerDimensions.x, chartContainerDimensions.y);
      }, 50),
    [chartContainerDimensions],
  );

  useEffect(() => {

    if (chartContainerDimensions.x == 0 && chartContainerDimensions.y == 0) {
      return;
    }

    chartSizeUpdate();

    return () => {
      chartSizeUpdate.cancel();
    };
  }, [chartContainerDimensions]);

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

  // Each product is pinned to one of the two y-axes, so an axis can only show a currency
  // symbol when every product plotted against it shares the same currency.
  const axisCurrencySymbols = useMemo(() => {
    const symbolForAxis = (axis: 0 | 1): string => {
      const currencyCodes = Object.values(products)
        .filter((product) => (productYAxes[product.productId] ?? 0) === axis)
        .map((product) => product.currency as string);

      return CurrencyHelper.getSymbol(CurrencyHelper.getCommonCurrency(currencyCodes));
    };

    return [symbolForAxis(0), symbolForAxis(1)];
  }, [products, productYAxes]);

  const defaultOptions: Highcharts.Options = {
    // chart: {
    //   zoomType: 'x',
    // },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      type: 'datetime',
      labels: {
      format: '{value:%b %Y}',
      rotation: -45,
      },
    },
    yAxis: [
      {
      title: {
        text: undefined,
      },
      labels: {
        formatter: function() {
          return axisCurrencySymbols[0] + parseFloat(`${this.value}`).toFixed(2);
        }
      }
      },
      {
      title: {
        text: undefined,
      },
      opposite: true,
      labels: {
        formatter: function() {
          return axisCurrencySymbols[1] + parseFloat(`${this.value}`).toFixed(2);
        }
      }
      },
    ],
    tooltip: {
      shared: true,
      pointFormatter: function (this: Highcharts.Point): string {
        const currencySymbol = ((this.series.options as any).custom?.currencySymbol as string) ?? '';
        const pointValue = this.y;
        const formattedValue = pointValue == null ? '' : `${currencySymbol}${Highcharts.numberFormat(pointValue, 2)}`;
        return `<span style="color:${this.color as string}">\u25CF</span> ${this.series.name}: <b>${formattedValue}</b><br/>`;
      },
    },
    // exporting: {
    //   enabled: true
    // }
    exporting: {
      buttons: {
      contextButton: {
        enabled: true,
        menuItems: [
          // 'viewFullscreen',
          'downloadPNG',
          'downloadSVG',
        ],
      },
      },
    },
    //   colors: StoneXColors,
  };

  const dateTimeToTimestamp = (date: string) => dayjs(date).unix() * 1000;

  const toggleYAxis = (productId: number) => {
    const newAxis: 0 | 1 = productYAxes[productId] == 0 ? 1 : 0;
    const newAxes = { ...productYAxes, [productId]: newAxis };
    setProductYAxes(newAxes);
  };

  const dailyPricesToArray = (quoteDatePrices: QuoteDateValues): [number, Price][] => {
    const prices: [number, Price][] = [];

    for (const quoteDate in quoteDatePrices) {
      const quoteDateValue = quoteDatePrices[quoteDate];
      let timestamp = dateTimeToTimestamp(quoteDate);
      prices.push([timestamp, quoteDateValue.price]);
    }

    return prices.sort((a, b) => a[0] - b[0]);
  };

  const historicalSeriesToChartSeries = (series: Series): Highcharts.SeriesOptionsType => {
    return {
      type: 'line',
      name: series.label,
      data: dailyPricesToArray(series.quoteDateValues),
      yAxis: productYAxes[series.product.productId],
      zIndex: 5,
      custom: { currencySymbol: CurrencyHelper.getSymbol(series.product.currency) },
    } as Highcharts.SeriesOptionsType;
  };

  const MATRIX_COLORS: Record<number, string> = {
    0: '#1a5490', 5: '#1a5490', 10: '#3578b5', 15: '#3578b5',
    20: '#6ba3d6', 25: '#6ba3d6', 30: '#a8cce8', 35: '#a8cce8',
    40: '#d6e6f5', 45: '#d6e6f5', 50: '#fce4b8', 55: '#fce4b8',
    60: '#f7c574', 65: '#f7c574', 70: '#f5a623', 75: '#f5a623',
    80: '#e88a2d', 85: '#e88a2d', 90: '#d45800', 95: '#d45800', 100: '#d45800',
  };

  const MATRIX_OPACITY: Record<number, string> = {
    0: '44', 5: '44', 10: '55', 15: '55',
    20: '77', 25: '77', 30: '99', 35: '99',
    40: 'FF', 45: 'FF', 50: 'FF', 55: 'FF',
    60: '99', 65: '99', 70: '77', 75: '77',
    80: '55', 85: '55', 90: '44', 95: '44', 100: '44',
  };

  const buildMatrixArearangeSeries = (): Highcharts.SeriesOptionsType[] => {
    if (!matrixResult || !matrixResult.graphPoints || matrixResult.graphPoints.length === 0) { return []; }

    const startTime = new Date(matrixResult.startDate!).getTime();
    const allGraphPoints = narrowGraphPoints(matrixResult.graphPoints);
    const firstInRangeIndex = allGraphPoints.findIndex((p) => new Date(p.calculationDate).getTime() >= startTime);
    const clipIndex = firstInRangeIndex > 0 ? firstInRangeIndex - 1 : 0;
    const graphPoints = allGraphPoints.slice(clipIndex);
    if (graphPoints.length === 0) { return []; }

    const termIndex = 0;
    const sortedRanks = [...selectedPercentiles].sort((a, b) => a - b);

    const series: Highcharts.SeriesOptionsType[] = [];

    for (let i = 0; i < sortedRanks.length - 1; i++) {
      const lowPercent = sortedRanks[i];
      const highPercent = sortedRanks[i + 1];
      const lowRank = lowPercent / 100;
      const highRank = highPercent / 100;

      const baseColor = MATRIX_COLORS[lowPercent] ?? '#ccc';
      const opacity = MATRIX_OPACITY[lowPercent] ?? '99';

      const data = graphPoints.map((point) => {
        const termResult = point.terms[termIndex];
        if (!termResult) { return [new Date(point.calculationDate).getTime(), 0, 0]; }
        const lowValue = termResult.weightedDeciles.find((d) => Math.abs(d.rank - lowRank) < 0.001)?.value ?? 0;
        const highValue = termResult.weightedDeciles.find((d) => Math.abs(d.rank - highRank) < 0.001)?.value ?? 0;
        return [new Date(point.calculationDate).getTime(), lowValue, highValue];
      });

      series.push({
        name: `${lowPercent}% - ${highPercent}%`,
        type: 'arearange',
        data: data,
        color: `${baseColor}${opacity}`,
        fillColor: `${baseColor}${opacity}`,
        lineColor: `${baseColor}${opacity}`,
        lineWidth: 1,
        marker: { enabled: false },
        showInLegend: false,
        step: 'left',
        label: { enabled: false },
        enableMouseTracking: false,
        zIndex: 1,
      } as Highcharts.SeriesArearangeOptions);
    }

    return series;
  };

  const buildMatrixPlotBandLabels = (): Highcharts.YAxisPlotBandsOptions[] => {
    if (!matrixResult || !matrixResult.graphPoints || matrixResult.graphPoints.length === 0) { return []; }

    const graphPoints = narrowGraphPoints(matrixResult.graphPoints);
    const lastPoint = graphPoints[graphPoints.length - 1];
    const termIndex = 0;
    const termResult = lastPoint?.terms[termIndex];
    if (!termResult) { return []; }

    const percentiles = termResult.weightedDeciles;
    const sortedPercentiles = [...selectedPercentiles].sort((a, b) => a - b);

    const plotBands: Highcharts.YAxisPlotBandsOptions[] = [];

    for (let i = 0; i < sortedPercentiles.length - 1; i++) {
      const lowPercent = sortedPercentiles[i];
      const highPercent = sortedPercentiles[i + 1];
      const lowRank = lowPercent / 100;
      const highRank = highPercent / 100;
      const lowValue = percentiles.find((d) => Math.abs(d.rank - lowRank) < 0.001)?.value ?? 0;
      const highValue = percentiles.find((d) => Math.abs(d.rank - highRank) < 0.001)?.value ?? 0;

      plotBands.push({
        from: lowValue,
        to: highValue,
        color: 'transparent',
        zIndex: 5,
        label: {
          text: `${lowPercent}% - ${highPercent}%`,
          style: { color: '#000', fontWeight: '600', fontSize: '11px' },
        },
      });
    }

    return plotBands;
  };

  const buildHistoricalChart = (allSeries: Series[]) => {
    let chartSeries: Highcharts.SeriesOptionsType[] = [];

    // Add decile arearange series first (so they render behind price lines)
    const decileSeries = buildMatrixArearangeSeries();
    chartSeries = [...decileSeries];

    for (const series of allSeries) {
      const seriesAsHighchartSeries = historicalSeriesToChartSeries(series);
      chartSeries = [...chartSeries, seriesAsHighchartSeries];
    }

    const plotBands = buildMatrixPlotBandLabels();

    const options: Highcharts.Options = {
      ...defaultOptions,
      series: chartSeries,
      yAxis: plotBands.length > 0 ? [
        {
          ...(defaultOptions.yAxis as Highcharts.YAxisOptions[])?.[0],
          plotBands: plotBands,
          gridZIndex: 0,
        },
        ...(defaultOptions.yAxis as Highcharts.YAxisOptions[])?.slice(1) ?? [],
      ] : defaultOptions.yAxis,
    };

    setChartOptions(options);
  };

  const forwardPricesToArray = (query: MarketViewQueryForwardCurveResponse): [number, Price][] => {
    const prices: [number, Price][] = [];

    query.rows!.forEach((q) => {
      const date = new Date(q.contractDate!.year!, q.contractDate!.month!, 1).toString();
      const timestamp = dateTimeToTimestamp(date);
      prices.push([timestamp!, q.price]);
    });

    return prices;
  };

  const forwardQueryToChartSeries = (query: MarkItViewQueryResult) => {
    return {
      type: 'line',
      name: query.product.name,
      data: forwardPricesToArray(query.query as MarketViewQueryForwardCurveResponse),
      yAxis: productYAxes[query.product.productId],
      custom: { currencySymbol: CurrencyHelper.getSymbol(query.product.currency) },
    } as Highcharts.SeriesOptionsType;
  };

  const buildForwardChart = (queries: MarkItViewQueryResult[]) => {
    let chartSeries: Highcharts.SeriesOptionsType[] = [];

    for (const query of queries) {
      chartSeries.push(forwardQueryToChartSeries(query));
    }

    const options: Highcharts.Options = {
      ...defaultOptions,
      series: chartSeries,
    };

    setChartOptions(options);
  };

  const buildChart = (allSeries: Series[]) => {
    //historical price set up comes from the series data, but forward comes from the original queries
    const historicalSeries = allSeries.filter((q) => q.queryType !== PriceQueryType.ForwardCurve);
    const forwardQueries = queries.filter((q) => q.queryType === PriceQueryType.ForwardCurve);

    //can't chart a mixture of both forward & regular curves.
    const hasHistorical = some(historicalSeries);
    const hasMatrix = !!matrixResult;

    if (hasHistorical || hasMatrix) {
      buildHistoricalChart(historicalSeries);
    } else {
      buildForwardChart(forwardQueries);
    }
  };

  const getQueryPriceSeries = (queryId: number, product: MarketViewProductModel, query: MarketViewQueryPriceResponse): Series[] => {
    const allContractDates = flatMap(query.rows, (q) => q.contractPrices!.map((r) => r.contractDate!.label!));
    const contractDates = uniq(allContractDates);

    let series: Series[] = [];

    contractDates?.forEach((contractDate, index) => {
      let quoteDatePrices: QuoteDateValues = {};

      query.rows?.forEach((q) => {
        const price = q.contractPrices?.find((r) => r.contractDate!.label! === contractDate)?.price;
        quoteDatePrices[q.quoteDate!] = {
          price: price
        };
      });

      const seriesData: Series = {
        queryId: `${queryId}_${index}`,
        label: `${product.name} - ${contractDate}`,
        quoteDateValues: quoteDatePrices,
        product: product,
        queryType: PriceQueryType.Price,
        showInChart: true,
        showInGrid: true
      };

      series.push(seriesData);
    });

    return series;
  };

  const getDailyPriceSeries = (queryId: number, product: MarketViewProductModel, query: MarketViewQueryDailyPricesResponse): Series[] => {
    // One assessment per quote date and no contract month, so this is a single series
    // against the product itself. Only the close price is plotted - the high and low the
    // endpoint also returns are not shown.
    const dailyPrices: QuoteDateValues = {};

    query.rows?.forEach((q) => {
      dailyPrices[q.quoteDate] = { price: q.closePrice };
    });

    return [
      {
        queryId: queryId,
        label: product.name,
        quoteDateValues: dailyPrices,
        product: product,
        queryType: PriceQueryType.DailyPrice,
        showInChart: true,
        showInGrid: true,
      },
    ];
  };

  const getAverageStripSeries = (queryId: number, product: MarketViewProductModel, query: MarketViewQueryStripAverageResponse): Series[] => {
    const dailyPrices: QuoteDateValues = {};
    let series: Series[] = [];

    query.rows?.forEach((q) => {
      dailyPrices[q.quoteDate!] = {price: q.price};
    });

    const seriesData: Series = {
      queryId: queryId,
      label: `${product.name} - ${query.contractDateRange!.startContractDate?.label} - ${query.contractDateRange!.endContractDate?.label ?? ''} Avg`,
      quoteDateValues: dailyPrices,
      product: product,
      queryType: PriceQueryType.StripAverage,
      showInChart: true,
      showInGrid: true
    };

    series.push(seriesData);

    return series;
  };

  const getCalendarStripAverageSeries = (queryId: number, product: MarketViewProductModel, query: MarketViewQueryCalendarStripAverageResponse): Series[] => {
    const years = uniq(query.rows?.flatMap((q) => q.yearPrices!.map((r) => r.year!)));

    let series: Series[] = [];

    years?.map((y) => {
      let dailyPrices: QuoteDateValues = {};

      query.rows?.forEach((q) => {
        const price = q.yearPrices?.find((r) => r.year === y)?.price;
        dailyPrices[q.quoteDate!] = {price: price};
      });

      const seriesData: Series = {
        queryId: `${queryId}_${y}`,
        label: `${product.name} - ${y} Strip Avg`,
        quoteDateValues: dailyPrices,
        product: product,
        queryType: PriceQueryType.CalendarStripAverage,
        showInChart: true,
        showInGrid: true
      };

      series.push(seriesData);
    });

    return series;
  };

  const getRollingMonthAheadSeries = (queryId: number, product: MarketViewProductModel, query: MarketViewQueryRollingMonthAheadResponse): Series[] => {
    const dailyPrices: QuoteDateValues = {};
    let series: Series[] = [];

    query.rows?.forEach((q) => {
      dailyPrices[q.quoteDate!] = {price: q.price};
    });

    const seriesData: Series = {
      queryId: queryId,
      label: `${product.name} - Rolling ${query.monthsAhead} ${query.monthsAhead == 1 ? 'Month' : 'Months'}`,
      quoteDateValues: dailyPrices,
      product: product,
      queryType: PriceQueryType.StripAverage,
      showInChart: true,
      showInGrid: true
    };

    series.push(seriesData);

    return series;
  };

  const getForwardCurveSeries = (queryId: number, product: MarketViewProductModel, query: MarketViewQueryForwardCurveResponse): Series[] => {
    let series: Series[] = [];

    query.rows?.forEach((q) => {
      const dailyPrices: QuoteDateValues = {};

      dailyPrices[query.quoteDate!] = {price: q.price};

      const seriesData: Series = {
        queryId: `${queryId}_${q.contractDate!.label!}`,
        label: `${product.name } - ${q.contractDate!.label!}`,
        quoteDateValues: dailyPrices,
        product: product,
        queryType: PriceQueryType.ForwardCurve,
        showInChart: true,
        showInGrid: true
      };

      series.push(seriesData);
    });

    return series;
  };

  const getSettlementsSeries = (queryId: number, product: MarketViewProductModel, query: MarketViewQuerySettlementsResponse): Series[] => {
    const dailyPrices: QuoteDateValues = {};
    const dailyValues: QuoteDateValues = {};
    let series: Series[] = [];

    query.rows?.forEach((q) => {
      dailyPrices[q.quoteDate!] = {price: q.price};
      dailyValues[q.quoteDate!] = {price: null, chartLabel: q.contractDate.label, gridCell: <>{q.contractDate.label}</>}
    });

    const priceSeriesData: Series = {
      queryId: queryId,
      label: `${product.name} - Settlement Prices`,
      quoteDateValues: dailyPrices,
      product: product,
      queryType: PriceQueryType.StripAverage,
      showInChart: true,
      showInGrid: true
    };

    const contractSeriesData: Series = {
      queryId: queryId + '_settlement_months',
      label: `${product.name} - Settlement Month`,
      quoteDateValues: dailyValues,
      product: product,
      queryType: PriceQueryType.StripAverage,
      showInChart: false,
      showInGrid: true
    };

    series.push(contractSeriesData);
    series.push(priceSeriesData);

    return series;
  };

  const buildQueryPriceDict = (queries: MarkItViewQueryResult[]): void => {
    let allSeriesDict: Series[] = [];

    queries.forEach((q) => {
      switch (q.queryType) {
        case PriceQueryType.Price:
          allSeriesDict = [...allSeriesDict, ...getQueryPriceSeries(q.id, q.product, q.query as MarketViewQueryPriceResponse)];
          break;
        case PriceQueryType.StripAverage:
          allSeriesDict = [...allSeriesDict, ...getAverageStripSeries(q.id, q.product, q.query as MarketViewQueryStripAverageResponse)];
          break;
        case PriceQueryType.CalendarStripAverage:
          allSeriesDict = [...allSeriesDict, ...getCalendarStripAverageSeries(q.id, q.product, q.query as MarketViewQueryCalendarStripAverageResponse)];
          break;
        case PriceQueryType.RollingMonthAhead:
          allSeriesDict = [...allSeriesDict, ...getRollingMonthAheadSeries(q.id, q.product, q.query as MarketViewQueryRollingMonthAheadResponse)];
          break;
        case PriceQueryType.Rolling12MonthStrip:
          allSeriesDict = [...allSeriesDict, ...getRollingMonthAheadSeries(q.id, q.product, q.query as MarketViewQueryRollingMonthAheadResponse)];
          break;
        case PriceQueryType.ForwardCurve:
          allSeriesDict = [...allSeriesDict, ...getForwardCurveSeries(q.id, q.product, q.query as MarketViewQueryForwardCurveResponse)];
          break;
        case PriceQueryType.Settlement:
          allSeriesDict = [...allSeriesDict, ...getSettlementsSeries(q.id, q.product, q.query as MarketViewQuerySettlementsResponse)];
          break;
        case PriceQueryType.DailyPrice:
          allSeriesDict = [...allSeriesDict, ...getDailyPriceSeries(q.id, q.product, q.query as MarketViewQueryDailyPricesResponse)];
          break;
      }
    });

    setAllQuerySeries(allSeriesDict);
  };

  const getAllQuoteDates = (allSeries: Series[]): string[] => {
    let allQuoteDates: string[] = [];

    for (const series of allSeries) {
      allQuoteDates = [...allQuoteDates, ...Object.keys(series.quoteDateValues)];
    }

    const uniqueQuoteDates = uniq(allQuoteDates).sort();
    return uniqueQuoteDates;
  };

  const queriesInGridDataFormat = (series: Series[]): Row[] => {
    const allQuoteDates = getAllQuoteDates(series);
    let rows: Row[] = [];

    allQuoteDates.forEach((qd) => {
      // A list of PriceData for each query.
      let quoteDateColumns: { [key: string]: PriceData } = {};

      for (const index in series) {
        const price = series[index].quoteDateValues[qd];
        quoteDateColumns[series[index].queryId] = price;
      }

      const row: Row = {
        quoteDate: qd,
        columns: quoteDateColumns,
      };

      rows.push(row);
    });

    return rows;
  };

  const buildTable = (allSeries: Series[]) => {

    allSeries = allSeries.filter(x => x.showInGrid);

    const dataInGridFormat = queriesInGridDataFormat(allSeries);

    let filteredDataInGridFormat = dataInGridFormat;

    if (!isEmployee) {
      const cutoff = dayjs().subtract(90, 'day');
      filteredDataInGridFormat = dataInGridFormat.filter(row => dayjs(row.quoteDate).isAfter(cutoff));
    }

    filteredDataInGridFormat.sort((a, b) => dayjs(b.quoteDate).valueOf() - dayjs(a.quoteDate).valueOf());

    setRowData(filteredDataInGridFormat);

    const seriesGrouping =  groupBy(allSeries, x => x.product.name);
    const groupedColumns = Object.entries(seriesGrouping).map(([groupingLabel, groupedSeries]: [string, Series[]]) => {
      return {
        headerName: groupingLabel,
        children: groupedSeries.map((series, index) => {
          return {
            headerName: series.label,
            flex: 1,
            cellRenderer: (params: ICellRendererParams<Row>) => {

              const cellData = params.data?.columns[series.queryId];

              if (cellData?.gridCell) {
                return cellData.gridCell;
              }
              else {
                return <StoneXNumber number={cellData?.price ?? undefined} decimals={3}/>
              }

            },
            valueGetter: (params: ICellRendererParams<Row>) => {
              return params.data?.columns[series.queryId]?.price ?? null;
            },
            cellClass: 'markItViewPrice',
          };
        }),
      };
    })

    const quoteDateHeader = {
      headerName: 'Quote Date',
      field: 'quoteDate',
      valueFormatter: (params: any) => dayjs(params.value).format('L'),
      cellClass: 'markItViewQuoteDate',
    };

    setColumnDefs([quoteDateHeader, ...groupedColumns]);
  };

  const setDefaultProductYAxes = (allSeries: Series[]) => {
    //just set all products to y-axis 1
    const axes: { [productId: number]: 0 | 1 } = {};

    allSeries.forEach((s) => {
      if (axes[s.product.productId]) return;
      axes[s.product.productId] = 0;
    });

    setProductYAxes(axes);
  };

  const setProductsDict = (queries: MarkItViewQueryResult[]) => {
    const products: { [productId: number]: MarketViewProductModel } = {};

    queries.forEach((q) => {
      products[q.product.productId] = q.product;
    });

    setProducts(products);
  };

  useEffect(() => {
    buildQueryPriceDict(queries);
    setProductsDict(queries);
  }, [queries]);

  useEffect(() => {
    setDefaultProductYAxes(allQuerySeries);
    buildChart(allQuerySeries);
    buildTable(allQuerySeries);
  }, [allQuerySeries]);

  useEffect(() => {
    buildChart(allQuerySeries);
  }, [productYAxes]);

  useEffect(() => {
    buildChart(allQuerySeries);
  }, [matrixResult]);

  useEffect(() => {
    if (matrixResult) {
      buildChart(allQuerySeries);
    }
  }, [selectedPercentiles]);

  return (
    <>
      <StoneXRow>
        <StoneXChart highcharts={Highcharts} options={chartOptions} width='1500px'/>
      </StoneXRow>
      
      <StoneXRow align="start">
        <Button
          variant='outlined'
          size="small"
          startIcon={<LineAxis />}
          onClick={(e: any) => {
            setShowingChartControls((p) => !p);
          }}
        >
          {showingChartControls ? 'Hide Chart Controls' : 'Show Chart Controls'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Download />}
          onClick={() => {
            // Export to Excel using ag-Grid API
            gridRef.current?.api.exportDataAsExcel({
              sheetName: 'Market Data',
              fileName: 'Mark-It View Export.xlsx',
              columnWidth: 200,
              processCellCallback: (params) => {
                // Excel date cells need an ISO value; the style below renders it as mm/dd/yyyy
                if (params.column.getColId() === 'quoteDate' && params.value) {
                  return dayjs(params.value).format('YYYY-MM-DDTHH:mm:ss');
                }
                return params.formatValue(params.value);
              },
            });
          }}
        >
          Export Data
        </Button>
      </StoneXRow>
      {showingChartControls && (
        <StoneXRow>
          <Card variant="outlined" sx={{ p: 1 }}>
            <CardContent sx={{ padding: '8px !important' }}>
              {Object.values(products).map((q) => (
                <Box 
                  key={q.productId} 
                  sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 0,
                  }}
                >
                  {q.name}
                  <IconButton 
                    size="small"
                    onClick={() => toggleYAxis(q.productId)}
                    color={productYAxes[q.productId] === 0 ? 'primary' : 'secondary'}
                    sx={{ minWidth: 40 }}
                  >
                    {productYAxes[q.productId] === 0 ? <LooksOne /> : <LooksTwo />}
                  </IconButton>
                </Box>
              ))}
              <StoneXDivider />
              <span className='helper-text'>Click on the number to toggle the chart axis</span>
            </CardContent>
          </Card>

          {matrixResult && (
            <MatrixChartControls
              selectedPercentiles={selectedPercentiles}
              onPercentilesChange={setSelectedPercentiles}
              minPercentile={minPercentile}
              onMinPercentileChange={setMinPercentile}
              maxPercentile={maxPercentile}
              onMaxPercentileChange={setMaxPercentile}
            />
          )}
        </StoneXRow>
      )}

      <div className="ag-theme-alpine" style={{ height: '800px' }}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          rowHeight={30}
          excelStyles={[
            {
              id: 'markItViewPrice',
              numberFormat: {
                format: '#,##0.000',
              },
            },
            {
              id: 'markItViewQuoteDate',
              dataType: 'DateTime',
              numberFormat: {
                // 'mm-dd-yy' is ag-grid's key for Excel built-in format 14, the locale short date (*3/14/2012).
                // Any other string is written as a Custom format instead of a Date.
                format: 'mm-dd-yy',
              },
            },
          ]}
        />
      </div>

      {matrixResult && matrixResult.graphPoints && matrixResult.graphPoints.length > 0 && (
        <MatrixTable
          results={narrowResults(matrixResult.results)}
          graphPoints={narrowGraphPoints(matrixResult.graphPoints).filter((p) => new Date(p.calculationDate).getTime() >= new Date(matrixResult.startDate!).getTime())}
          weighted={matrixResult.weighted ?? true}
          historyMode={true}
          selectedPercentiles={selectedPercentiles}
        />
      )}
    </>
  );
}
